import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router'
import { reactPlugin } from "./AppInsights";
import { AppInsightsErrorBoundary, AppInsightsContext } from "@microsoft/applicationinsights-react-js";

import Styles from './App.module.css'
import Spinner from './components/Spinner/Spinner'
import Ie from './components/Ie'

import { AuthProvider, useAuth } from './context/AuthContext'
import { PermissionsProvider, usePermissions } from './context/PermissionsContext'
import apiFetch from './api/apiFetch'
import AuthorizeRoute from './api/api-authorization/AuthorizeRoute';
import Navbar from './components/Navbar/Navbar'
import { ExercisesContext } from './ExercisesContext'

//pages
import HcpDetails from './pages/HcpDetails/HcpDetails'
import HcoDetails from './pages/HcoDetails/HcoDetails'
import Home from './pages/Home/Home'
import Search from './pages/Search/Search'
import ExUsSearch from './pages/ExUsSearch/ExUsSearch'
import ExUsDetails from './pages/ExUsDetails/ExUsDetails'
import Admin from './pages/Admin/Admin'
import Targeting from './pages/Targeting/Targeting'
import TargetingV2 from './pages/TargetingV2/TargetingV2'
import { FeatureGate } from './components/FeatureGate/FeatureGate'
import HomeV3 from './pages/HomeV3/HomeV3'
import ProfessionalsV3 from './pages/ProfessionalsV3/ProfessionalsV3'
import OrganizationsV3 from './pages/OrganizationsV3/OrganizationsV3'
import ExUsProfessionalsV3 from './pages/ExUsProfessionalsV3/ExUsProfessionalsV3'
import TargetingV3 from './pages/TargetingV3/TargetingV3'
import ExhibitsDisplaysV3 from './pages/ExhibitsDisplaysV3/ExhibitsDisplaysV3'
import AdminV3 from './pages/AdminV3/AdminV3'
import './global.css'

import Workflow from './pages/Workflow/Workflow'

// ── Stable route-level components ──────────────────────────────────────────
// Defined at module scope so the component *type* is always the same reference.
// Previously these were useMemo(() => () => <JSX/>, [volatile deps]) inside
// AppInner; when deps changed React saw a new component type and fully
// unmounted + remounted the page on every nav click that touched searchState.
// Moving to module scope means React re-renders (prop update) instead
// of remounting, which also stops the saveState → searchState → remount cycle.
// (ICY-11966)

interface SearchRouteProps {
    saveState: (s: unknown) => void
    searchState: unknown
    user: unknown
}
interface DetailsRouteProps {
    user: unknown
}
interface UserRouteProps { user: unknown }

function HomeRoute() {
    return (
        <FeatureGate flag="HomeV3" fallback={<Home />}>
            <HomeV3 />
        </FeatureGate>
    )
}
function HcpPageRoute({ saveState, searchState, user }: SearchRouteProps) {
    return (
        <FeatureGate flag="ProfessionalsV3" fallback={<Search saveState={saveState} searchState={searchState} user={user} />}>
            <ProfessionalsV3 />
        </FeatureGate>
    )
}
function HcpDetailsPageRoute({ user }: DetailsRouteProps) {
    return (
        <FeatureGate flag="ProfessionalsV3" fallback={<HcpDetails user={user} />}>
            <ProfessionalsV3 />
        </FeatureGate>
    )
}
function HcoPageRoute({ saveState, searchState, user }: SearchRouteProps) {
    return (
        <FeatureGate flag="OrganizationsV3" fallback={<Search saveState={saveState} searchState={searchState} user={user} />}>
            <OrganizationsV3 user={user} />
        </FeatureGate>
    )
}
function HcoDetailsPageRoute({ user }: DetailsRouteProps) {
    // V3 HCO details page is a future phase — always serve v2 HcoDetails.
    // HcoPageRoute (search) is feature-gated independently.
    return <HcoDetails user={user} />;
}
export function ExUsPageRoute({ saveState, searchState, user }: SearchRouteProps) {
    return (
        <FeatureGate flag="ExUsProfessionalsV3" fallback={<ExUsSearch saveState={saveState} searchState={searchState} user={user} />}>
            <ExUsProfessionalsV3 user={user} />
        </FeatureGate>
    )
}
function ExUsDetailsPageRoute({ user }: UserRouteProps) {
    return <ExUsDetails user={user} />;
}
function WorkflowPageRoute({ user }: UserRouteProps) {
    return (
        <FeatureGate flag="ExhibitsDisplaysV3" fallback={<Workflow user={user} />}>
            <ExhibitsDisplaysV3 />
        </FeatureGate>
    )
}
function TargetingPageRoute({ user }: UserRouteProps) {
    return (
        <FeatureGate flag="TargetingV3" fallback={<Targeting user={user} />}>
            <TargetingV3 />
        </FeatureGate>
    )
}
function AdminRoute() {
    return (
        <FeatureGate flag="AdminV3" fallback={<Admin />}>
            <AdminV3 />
        </FeatureGate>
    )
}
// ───────────────────────────────────────────────────────────────────────────

export default function App(props) {
    return (
        <AuthProvider>
            <PermissionsProvider>
                <AppInner {...props} />
            </PermissionsProvider>
        </AuthProvider>
    )
}

function AppInner(props) {

    const { user, token, isAuthenticated, isInitialized, signOut } = useAuth()
    const permissions = usePermissions()
    const [darkMode, setDarkMode] = useState(() =>
        localStorage.getItem('trinity_dark_mode') === 'true'
    )
    const [searchState, setSearchState] = useState(null)
    const searchStateRef = useRef(searchState)
    const [exercises, setExercises] = useState<Record<string, unknown[]>>({})
    const [ie] = useState(!!(window.document as Document & { documentMode?: number }).documentMode)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (isInitialized && isAuthenticated && user && token) {
            getTargetingExercises()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialized, isAuthenticated])

    useEffect(() => {
        document.body.classList.add('brand-refresh')
        if (darkMode) {
            document.body.classList.add('dark-mode')
        } else {
            document.body.classList.remove('dark-mode')
        }
    }, [darkMode])

    const toggleDarkMode = () => {
        setDarkMode(prev => {
            const next = !prev
            localStorage.setItem('trinity_dark_mode', String(next))
            return next
        })
    }

    const getTargetingExercises = () => {
        const { appAdmin, multiExercise } = permissions.targeting.global
        const { exercisePanel } = permissions.targeting.home

        const isAppAdmin = appAdmin.roles.includes(user.role)
        const isMultiExerciseAdmin =
            multiExercise.adminLevel.roles.includes(user.role) &&
            multiExercise.adminLevel.teams.includes(user.team)
        const isAdminUser = isAppAdmin || isMultiExerciseAdmin
        const isMultiExerciseUser =
            !isAdminUser && multiExercise.userLevel.teams.includes(user.team)

        const hasExerciseAccess =
            isAdminUser ||
            isMultiExerciseUser ||
            exercisePanel.include.roles.includes(user.role) ||
            (exercisePanel.include.teams.includes(user.team) &&
                !exercisePanel.exclude.roles.includes(user.role))

        if (hasExerciseAccess) {
            const { elevatedViewers, crossTeamExerciseAccess = {} } = permissions.targeting.global
            const isElevatedViewer = elevatedViewers.roles.includes(user.role)
            const crossAccess = isElevatedViewer ? crossTeamExerciseAccess : {}
            const teams = isAppAdmin
                ? undefined
                : (crossAccess[String(user.team)] ?? [String(user.team)])
            apiFetch('/api/v2/targeting/get-all-exercises', { teams, UserEmail: user.email }).then((results) => {
                if (results) {
                    for (let i = 0; i < results.length; i++) {
                        results[i].END_DATE = results[i].END_DATE.slice(0, 10).replace(/-/g, '/').replace(/\//g, '-')
                        results[i].START_DATE = results[i].START_DATE.slice(0, 10).replace(/-/g, '/').replace(/\//g, '-')
                    }
                    if (!isAppAdmin) {
                        apiFetch('/api/v2/targeting/get-my-substitutions', { UserEmail: user.email }).then((subs) => {
                            const subList = subs || []
                            const annotated = results.map(ex => {
                                if (subList.length > 0 && String(ex.TEAM) !== String(user.team)) {
                                    const match = subList.find(s =>
                                        s.VACANT_TERR_ID && String(s.VACANT_TERR_ID).startsWith(String(ex.TEAM))
                                    )
                                    return match ? { ...ex, vacantTerrID: match.VACANT_TERR_ID } : ex
                                }
                                else if (subList.length > 0 && String(ex.TEAM) === String(user.team)) {
                                    // Require the vacant territory to belong to the same team as the exercise
                                    // so a cross-team substitution (e.g. T13 record for a T21 rep) doesn't
                                    // accidentally override same-team exercises with the wrong territory.
                                    const match = subList.find(s =>
                                        s.VACANT_TERR_ID &&
                                        String(s.VACANT_TERR_ID).startsWith(String(ex.TEAM)) &&
                                        String(s.VACANT_TERR_ID) !== user.terr
                                    )
                                    return match ? { ...ex, vacantTerrID: match.VACANT_TERR_ID } : ex
                                }
                                return ex
                            })
                            setExercises({ ...exercises, targeting: annotated })
                            setLoaded(true)
                        })
                    } else {
                        setExercises({ ...exercises, targeting: results })
                        setLoaded(true)
                    }
                }
            })
        } else {
            setLoaded(true)
        }
    }

    const saveState = useCallback((newSearchState) => {
        searchStateRef.current = newSearchState
        setSearchState(newSearchState)
    }, [])

    if (ie) {
        return <Ie />
    }

    const api_regex = /^\/api\/.*/
    const scalar_regex = /^\/scalar\/.*/
    // if using "/api/" in the pathname, don't use React Router
    if (api_regex.test(window.location.pathname) ||
        scalar_regex.test(window.location.pathname)
            ) {
        return <div /> // must return at least an empty div
    } else {
        if (!isInitialized) {
            return <Spinner />
        } else {
            return (
                <AppInsightsErrorBoundary onError={() => <Spinner />} appInsights={reactPlugin}>
                    <AppInsightsContext.Provider value={reactPlugin}>
                        <Router>
                            <ExercisesContext.Provider value={exercises}>
                            <div className={Styles.App}>
                                <div className={Styles.MainView}>
                                    <AuthorizeRoute component={Navbar} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                                    <Routes>
                                        <Route path='/' element={
                                            <AuthorizeRoute component={HomeRoute} />
                                        } />
                                        <Route path='/hcp' element={
                                            <AuthorizeRoute component={HcpPageRoute} saveState={saveState} searchState={searchState} user={user} />
                                        } />
                                        <Route path='/hcp/details/:PROF_ID' element={
                                            <AuthorizeRoute component={HcpDetailsPageRoute} user={user} />
                                        } />
                                        <Route path='/hco' element={
                                            <AuthorizeRoute component={HcoPageRoute} saveState={saveState} searchState={searchState} user={user} />
                                        } />
                                        <Route path='/hco/details/:ORG_ID' element={
                                            <AuthorizeRoute component={HcoDetailsPageRoute} user={user} />
                                        } />
                                        <Route path='/exus' element={
                                            <AuthorizeRoute component={ExUsPageRoute} saveState={saveState} searchState={searchState} user={user} />
                                        } />
                                        <Route path='/exus/details/:PROF_ID' element={
                                            <AuthorizeRoute component={ExUsDetailsPageRoute} user={user} />
                                        } />
                                        <Route path='/Workflow/*' element={
                                            <AuthorizeRoute component={WorkflowPageRoute} user={user} />
                                        } />
                                        <Route path='/targeting/*' element={
                                            <AuthorizeRoute component={TargetingPageRoute} user={user} />
                                        } />
                                        <Route path='/targeting-v2/*' element={
                                            <AuthorizeRoute component={TargetingV2} user={user} />
                                        } />
                                        <Route path='/admin' element={
                                            <AuthorizeRoute component={AdminRoute} />
                                        } />
                                    </Routes>
                                </div>
                            </div>
                            </ExercisesContext.Provider>
                        </Router>
                    </AppInsightsContext.Provider>
                </AppInsightsErrorBoundary>
            )
        }
    }
}